import * as React from "react";
import { Action } from "./Action.js";
import { OTRS } from "./OTRS.js";
import * as cls from "./Accessibility.module.scss";
import { renderToStaticMarkup } from "react-dom/server";
import {
  FaVolumeOff,
  FaVolumeUp,
  FaRegImage,
  FaBan,
  FaAdjust,
} from "react-icons/fa";
import { TbLetterSpacing, TbLineHeight } from "react-icons/tb";
import { PiUserSoundDuotone } from "react-icons/pi";
export { Accessibility };

const fontList = [1, 1.25, 1.5, 2.0];
const fontLabel = [
  "100 процентов",
  "125 процентов",
  "150 процентов",
  "200 процентов",
];
const fontAbbr = ["100%", "125%", "150%", "200%"];
const ACCENT_COLOR = "#3B7CBE";
const themeList = [
  { label: "Цветная", abbr: "Ц", bg: "#EAF0F5" },
  { label: "Белая", abbr: "Б", fg: "black", bg: "white" },
  { label: "Светлая", abbr: "С", fg: "#657B83", bg: "#FDF6E3" },
  { label: "Тёмная", abbr: "Т", fg: "#839496", bg: "#002B36" },
  { label: "Чёрная", abbr: "Ч", fg: "white", bg: "black" },
  { label: "Чёрно-белая", abbr: "Ч/Б", filter: "grayscale(100%)" },
  { label: "Инверсия по яркости", abbr: "И", filter: "invert()" },
  { label: "Синий на голубом", abbr: "С/Г", fg: "#0000CD", bg: "#CFE8FF" },
  { label: "Жёлтый на чёрном", abbr: "Ж/Ч", fg: "#FFFF00", bg: "#000000" },
];
const speechLabel = ["Выключено", "Включено"];
const speechAbbr = ["Выкл", "Вкл"];

const fontFamilyList = ["inherit", "'Courier New', monospace"];
const fontFamilyLabel = ["Шрифт без засечек", "Моноширинный шрифт"];
const fontFamilyAbbr = ["Aa", "Aa"];

const spacingList = ["normal", "0.05em", "0.15em"];
const spacingLabel = [
  "Обычный межбуквенный интервал",
  "Средний межбуквенный интервал",
  "Большой межбуквенный интервал",
];

const lineHeightList = ["normal", "1.6", "2.2"];
const lineHeightLabel = [
  "Обычный межстрочный интервал",
  "Средний межстрочный интервал",
  "Большой межстрочный интервал",
];

const imagesLabel = [
  "Обычные изображения",
  "Скрыть изображения",
  "Чёрно-белые изображения",
];

// Признаки "мусорного" alt, который по факту равнозначен отсутствию
const JUNK_ALT_PATTERNS = [
  /^image$/i,
  /^photo$/i,
  /^picture$/i,
  /^img$/i,
  /^рисунок$/i,
  /^изображение$/i,
  /^фото$/i,
  /^\s*$/,
];

/**
 * Озвучивает текст через синтезатор речи браузера, если голосовое
 * сопровождение включено.
 * @param {string} text - текст для озвучивания.
 * @param {boolean} enabled - включено ли озвучивание.
 */
let currentUtterance = null;
let voices = [];
function loadVoices() {
  voices = window.speechSynthesis.getVoices();
}
if (typeof window !== "undefined" && window.speechSynthesis) {
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
}

function speak(text, enabled) {
  if (!enabled) return;
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  window.speechSynthesis.cancel();
  currentUtterance = new SpeechSynthesisUtterance(text);
  currentUtterance.lang = "ru-RU";
  currentUtterance.volume = 1;
  const ruVoice = voices.find((v) => v.lang.startsWith("ru"));
  if (ruVoice) currentUtterance.voice = ruVoice;
  window.speechSynthesis.speak(currentUtterance);
}

const FILENAME_LIKE = /\.(jpe?g|png|gif|svg|webp|bmp)$/i;

function isAltInvalid(alt) {
  if (alt == null) return true; // атрибута нет вообще
  if (FILENAME_LIKE.test(alt)) return true;
  return JUNK_ALT_PATTERNS.some((re) => re.test(alt));
}

function guessAltText(img) {
  const figcaption = img
    .closest("figure")
    ?.querySelector("figcaption")
    ?.textContent?.trim();
  if (figcaption) return figcaption;

  const parentTitle = img.parentElement?.getAttribute("title");
  if (parentTitle) return parentTitle;

  const labelledBy = img.getAttribute("aria-labelledby");
  if (labelledBy) {
    const labelText = document.getElementById(labelledBy)?.textContent?.trim();
    if (labelText) return labelText;
  }
  const siblingText = img.nextElementSibling?.textContent?.trim();
  if (siblingText && siblingText.length < 100) return siblingText;

  return null;
}

function ensureAltTags() {
  if (typeof document === "undefined") return;
  const images = document.querySelectorAll("img");
  images.forEach((img) => {
    const currentAlt = img.getAttribute("alt");
    if (!isAltInvalid(currentAlt)) return;
    const guessed = guessAltText(img);
    img.setAttribute("alt", guessed || "Изображение без описания");
    img.setAttribute("data-a11y-alt-fallback", guessed ? "guessed" : "generic");
  });
}

// Кастомный курсор для режима озвучивания
let speechCursorUrl = null;
function getSpeechCursor() {
  if (speechCursorUrl) return speechCursorUrl;
  if (typeof window === "undefined") return "auto";
  const svg = renderToStaticMarkup(<PiUserSoundDuotone size={28} />);
  const encoded = window.btoa(unescape(encodeURIComponent(svg)));
  speechCursorUrl = `url("data:image/svg+xml;base64,${encoded}") 14 14, pointer`;
  return speechCursorUrl;
}

let hoverTimeout = null;
let lastSpokenText = null;

function getTextNodeAtPoint(x, y) {
  let range;
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(x, y);
  } else if (document.caretPositionFromPoint) {
    const pos = document.caretPositionFromPoint(x, y);
    if (!pos) return null;
    range = document.createRange();
    range.setStart(pos.offsetNode, pos.offset);
  }
  if (!range || !range.startContainer) return null;
  const node = range.startContainer;
  return node.nodeType === Node.TEXT_NODE ? node : null;
}

function getReadableTarget(x, y) {
  const hit = document.elementFromPoint(x, y);
  if (!hit) return null;

  const labelEl = hit.closest?.("[aria-label], [alt], [title]");
  if (labelEl) {
    const text =
      labelEl.getAttribute("aria-label") ||
      labelEl.getAttribute("alt") ||
      labelEl.getAttribute("title");
    if (text) return { text: text.trim(), mode: "label" };
  }

  const textNode = getTextNodeAtPoint(x, y);
  if (textNode) {
    const text = textNode.textContent.trim();
    if (text) {
      const range = document.createRange();
      range.selectNodeContents(textNode);
      return { text, mode: "underline", rectEl: range };
    }
  }

  return null;
}

let overlayEl = null;
function getOverlay() {
  if (overlayEl) return overlayEl;
  overlayEl = document.createElement("div");
  overlayEl.style.position = "fixed";
  overlayEl.style.pointerEvents = "none";
  overlayEl.style.zIndex = "999999";
  overlayEl.style.borderBottom = "2px solid #3B7CBE";
  overlayEl.style.boxSizing = "border-box";
  overlayEl.style.display = "none";
  document.body.appendChild(overlayEl);
  return overlayEl;
}

function showUnderline(rectEl) {
  const rect = rectEl.getBoundingClientRect();
  const overlay = getOverlay();
  overlay.style.left = `${rect.left}px`;
  overlay.style.top = `${rect.bottom - 2}px`;
  overlay.style.width = `${rect.width}px`;
  overlay.style.height = "2px";
  overlay.style.display = "block";
}

function hideOverlay() {
  if (overlayEl) overlayEl.style.display = "none";
}

function attachGlobalSpeech(enabled) {
  if (typeof document === "undefined") return;

  function handleMove(event) {
    if (!enabled) return;
    const target = getReadableTarget(event.clientX, event.clientY);
    if (!target) {
      document.body.style.cursor = "";
      hideOverlay();
      lastSpokenText = null;
      return;
    }
    document.body.style.cursor = getSpeechCursor();
    if (target.mode === "underline") {
      showUnderline(target.rectEl);
    } else {
      hideOverlay();
    }

    if (target.text === lastSpokenText) return;
    lastSpokenText = target.text;

    clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => speak(target.text, true), 80);
  }

  function handleLeave() {
    document.body.style.cursor = "";
    hideOverlay();
    lastSpokenText = null;
  }

  document.addEventListener("mousemove", handleMove);
  document.addEventListener("mouseleave", handleLeave);

  return () => {
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseleave", handleLeave);
    clearTimeout(hoverTimeout);
    document.body.style.cursor = "";
    hideOverlay();
    lastSpokenText = null;
  };
}

/**
 * Отображает панель "Средства доступности" (в режиме доступности),
 * или содержимое элемента (в обычном режиме).
 * Туда помещают основное меню для обычного режима.
 * @summary Панель Средства доступности.
 * @returns {JSX.Element} Элемент JSX.
 * @example
 * <Accessibility/>
 * @author Алексей Васьковский <avaskovskiy@umney.ru>
 */
function Accessibility({ home = "https://roweb.online/personal" }) {
  const HOME = new Action({
    name: "Домой",
    icon: "home",
    href: home,
  });
  const menu = [HOME, ...Accessibility.menu, OTRS.OPEN, Accessibility.CLOSE];
  const [active, setActive] = React.useState(
    parseInt(
      localStorage.getItem("personal-studio-components.accessibility.active") ||
        "0",
    ),
  );
  const [font, setFont] = React.useState(
    parseInt(
      localStorage.getItem("personal-studio-components.accessibility.font") ||
        "0",
    ),
  );
  const [color, setColor] = React.useState(
    parseInt(
      localStorage.getItem("personal-studio-components.accessibility.color") ||
        "0",
    ),
  );
  const [speech, setSpeech] = React.useState(
    parseInt(
      localStorage.getItem("personal-studio-components.accessibility.speech") ||
        "0",
    ),
  );
  const [fontFamily, setFontFamily] = React.useState(
    parseInt(
      localStorage.getItem(
        "personal-studio-components.accessibility.fontFamily",
      ) || "0",
    ),
  );
  const [spacing, setSpacing] = React.useState(
    parseInt(
      localStorage.getItem(
        "personal-studio-components.accessibility.spacing",
      ) || "0",
    ),
  );
  const [lineHeight, setLineHeight] = React.useState(
    parseInt(
      localStorage.getItem(
        "personal-studio-components.accessibility.lineHeight",
      ) || "0",
    ),
  );
  const [images, setImages] = React.useState(
    parseInt(
      localStorage.getItem("personal-studio-components.accessibility.images") ||
        "0",
    ),
  );

  const body = document.body;
  if (font) body.style.setProperty("zoom", fontList[font]);
  else body.style.removeProperty("zoom");

  const currentTheme = themeList[color];
  const needsColorOverride = !!(currentTheme.fg && currentTheme.bg);

  if (needsColorOverride) {
    body.classList.add("accessibility_mode");
  } else {
    body.classList.remove("accessibility_mode");
  }
  body.classList.toggle("accessibility_theme_filter", !!currentTheme.filter);
  const html = document.documentElement;
  if (currentTheme.filter) {
    html.style.setProperty("filter", currentTheme.filter);
  } else {
    html.style.removeProperty("filter");
  }
  if (currentTheme.fg && currentTheme.bg) {
    body.style.setProperty("color", currentTheme.fg);
    body.style.setProperty("background", currentTheme.bg);
    body.style.setProperty("--accessible-foreground", currentTheme.fg);
    body.style.setProperty("--accessible-background", currentTheme.bg);
  } else {
    body.style.removeProperty("color");
    body.style.removeProperty("background");
    body.style.removeProperty("--accessible-foreground");
    body.style.removeProperty("--accessible-background");
  }

  body.style.setProperty("font-family", fontFamilyList[fontFamily]);
  body.style.setProperty("letter-spacing", spacingList[spacing]);
  body.style.setProperty("line-height", lineHeightList[lineHeight]);

  body.classList.toggle("accessibility_images_hidden", images === 1);
  body.classList.toggle("accessibility_images_grayscale", images === 2);

  React.useEffect(() => {
    addEventListener("beforeprint", () => {
      if (active) {
        addEventListener("afterprint", () => {
          setActive(true);
        });
        setActive(false);
      }
    });
  }, []);
  React.useEffect(() => {
    if (!active) return;
    ensureAltTags();
    const observer = new MutationObserver(() => ensureAltTags());
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["alt", "src"],
    });
    return () => observer.disconnect();
  }, [active]);

  Accessibility.onOpen = (event) => {
    event.preventDefault();
    localStorage.setItem(
      "personal-studio-components.accessibility.active",
      active ? 0 : 1,
    );
    setActive(!active);
  };
  Accessibility.onClose = (event) => {
    event.preventDefault();
    localStorage.setItem("personal-studio-components.accessibility.active", 0);
    setActive(false);
  };
  function onFont(font) {
    return (event) => {
      event.preventDefault();
      localStorage.setItem(
        "personal-studio-components.accessibility.font",
        font,
      );
      setFont(font);
      speak(`Масштаб: ${fontLabel[font]}`, speech);
    };
  }
  function onColor(color) {
    return (event) => {
      event.preventDefault();
      localStorage.setItem(
        "personal-studio-components.accessibility.color",
        color,
      );
      setColor(color);
      speak(themeList[color].label, speech);
    };
  }
  function onFontFamily(event) {
    event.preventDefault();
    const next = fontFamily ? 0 : 1;
    localStorage.setItem(
      "personal-studio-components.accessibility.fontFamily",
      next,
    );
    setFontFamily(next);
    speak(fontFamilyLabel[next], speech);
  }
  function onSpacing(event) {
    event.preventDefault();
    const next = (spacing + 1) % spacingList.length;
    localStorage.setItem(
      "personal-studio-components.accessibility.spacing",
      next,
    );
    setSpacing(next);
    speak(spacingLabel[next], speech);
  }
  function onLineHeight(event) {
    event.preventDefault();
    const next = (lineHeight + 1) % lineHeightList.length;
    localStorage.setItem(
      "personal-studio-components.accessibility.lineHeight",
      next,
    );
    setLineHeight(next);
    speak(lineHeightLabel[next], speech);
  }
  function onImages(value) {
    return (event) => {
      event.preventDefault();
      localStorage.setItem(
        "personal-studio-components.accessibility.images",
        value,
      );
      setImages(value);
      speak(imagesLabel[value], speech);
    };
  }
  function onReset(event) {
    event.preventDefault();
    localStorage.setItem("personal-studio-components.accessibility.font", 0);
    localStorage.setItem("personal-studio-components.accessibility.color", 0);
    localStorage.setItem("personal-studio-components.accessibility.speech", 0);
    localStorage.setItem(
      "personal-studio-components.accessibility.fontFamily",
      0,
    );
    localStorage.setItem("personal-studio-components.accessibility.spacing", 0);
    localStorage.setItem(
      "personal-studio-components.accessibility.lineHeight",
      0,
    );
    localStorage.setItem("personal-studio-components.accessibility.images", 0);
    setFont(0);
    setColor(0);
    setSpeech(0);
    setFontFamily(0);
    setSpacing(0);
    setLineHeight(0);
    setImages(0);
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    Accessibility.onClose(event);
  }
  function onSpeech(event) {
    event.preventDefault();
    const next = speech ? 0 : 1;
    localStorage.setItem(
      "personal-studio-components.accessibility.speech",
      next,
    );
    setSpeech(next);
    speak(`Озвучивание: ${speechLabel[next]}`, next);
  }

  const systemMenu = [];
  for (const menuItem of menu) {
    const action = new Action(menuItem);
    const name = action.name;
    const attrs = action.getAnchorAttributes();
    systemMenu.push(
      <a key={name} className={cls.accessibility_action} {...attrs}>
        {name}
      </a>,
    );
  }
  const fontMenu = [];
  for (let i = 0; i < fontList.length; ++i) {
    const name = fontLabel[i];
    const icon = fontAbbr[i];
    const style = {};
    if (i == font) style.borderColor = ACCENT_COLOR;
    fontMenu.push(
      <a
        key={name}
        aria-label={name}
        title={name}
        className={cls.accessibility_action}
        style={style}
        onClick={onFont(i)}
      >
        {icon}
      </a>,
    );
  }
  const colorMenu = themeList.map((theme, i) => {
    const style = {};
    if (i === color) style.borderColor = ACCENT_COLOR;
    if (theme.filter) style.filter = theme.filter;
    if (theme.fg && theme.bg) {
      style.color = theme.fg;
      style.background = theme.bg;
    }
    return (
      <a
        key={theme.label}
        aria-label={theme.label}
        title={theme.label}
        className={cls.accessibility_action}
        style={style}
        onClick={onColor(i)}
      >
        {theme.abbr}
      </a>
    );
  });
  const speechMenu = [];
  for (let i = 0; i < speechAbbr.length; ++i) {
    const name = speechLabel[i];
    const icon = speechAbbr[i];
    const style = {};
    if (i == speech) style.borderColor = ACCENT_COLOR;
    speechMenu.push(
      <a
        key={name}
        aria-label={name}
        title={name}
        className={cls.accessibility_action}
        style={style}
        onClick={i === speech ? undefined : onSpeech}
      >
        {icon}
      </a>,
    );
  }
  const imagesMenu = [
    { icon: <FaRegImage size={19} />, value: 0 },
    { icon: <FaBan size={19} />, value: 1 },
    { icon: <FaAdjust size={19} />, value: 2 },
  ].map(({ icon, value }) => {
    const name = imagesLabel[value];
    const style = {};
    if (value === images) style.borderColor = ACCENT_COLOR;
    return (
      <a
        key={name}
        aria-label={name}
        title={name}
        className={cls.accessibility_action}
        style={style}
        onClick={onImages(value)}
      >
        {icon}
      </a>
    );
  });

  React.useEffect(() => {
    const detach = attachGlobalSpeech(!!speech);
    return () => {
      if (detach) detach();
    };
  }, [speech]);
  if (!active) return null;
  return (
    <div className={cls.accessibility_navbar}>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>Масштаб:</span>
        {fontMenu}
      </span>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>Тема:</span>
        {colorMenu}
      </span>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>Шрифт:</span>
        <a
          aria-label={fontFamilyLabel[fontFamily]}
          title={fontFamilyLabel[fontFamily]}
          className={cls.accessibility_action}
          style={
            fontFamily
              ? { borderColor: ACCENT_COLOR, fontFamily: fontFamilyList[1] }
              : {}
          }
          onClick={onFontFamily}
        >
          {fontFamilyAbbr[fontFamily]}
        </a>
        <a
          aria-label={spacingLabel[spacing]}
          title={spacingLabel[spacing]}
          className={cls.accessibility_action}
          style={spacing ? { borderColor: ACCENT_COLOR } : {}}
          onClick={onSpacing}
        >
          <TbLetterSpacing size={19} />
          <sub style={{ fontSize: "0.6em", marginLeft: 2 }}>{spacing + 1}</sub>
        </a>
        <a
          aria-label={lineHeightLabel[lineHeight]}
          title={lineHeightLabel[lineHeight]}
          className={cls.accessibility_action}
          style={lineHeight ? { borderColor: ACCENT_COLOR } : {}}
          onClick={onLineHeight}
        >
          <TbLineHeight size={19} />
          {/* <sub style={{ fontSize: "0.6em", marginLeft: 2 }}>{spacing + 1}</sub> */}
        </a>
      </span>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>Изображения:</span>
        {imagesMenu}
      </span>
      <span className={cls.accessibility_section}>
        <span className={cls.accessibility_label}>Синтез речи:</span>
        <a
          aria-label="Выключить озвучивание"
          title="Выключить озвучивание"
          className={cls.accessibility_action}
          style={!speech ? { borderColor: ACCENT_COLOR } : {}}
          onClick={speech ? onSpeech : undefined}
        >
          <FaVolumeOff size={19} />
        </a>
        <a
          aria-label="Включить озвучивание"
          title="Включить озвучивание"
          className={cls.accessibility_action}
          style={speech ? { borderColor: ACCENT_COLOR } : {}}
          onClick={!speech ? onSpeech : undefined}
        >
          <FaVolumeUp size={19} />
        </a>
      </span>
      <span className={cls.accessibility_section}>
        <a
          aria-label="Сброс"
          title="Сброс"
          className={cls.accessibility_action}
          onClick={onReset}
        >
          Сброс
        </a>
      </span>
      {/*
        <span className={cls.accessibility_section}>
          <span className={cls.accessibility_label}>
            Меню:
          </span>
          {systemMenu}
        </span>*/}
    </div>
  );
}
Accessibility.menu = [];
/**
 * Пункт меню "Средства доступности" для перехода в режим доступности.
 * @type {Action}
 * @see {@link Action.js}
 */
Accessibility.OPEN = new Action({
  name: "Средства доступности",
  icon: "eye",
  href: "#accessibility_mode",
  onClick: (event) => {
    Accessibility.onOpen(event);
  },
});
/**
 * Пункт меню "Закрыть" для закрытия панели.
 * @type {Action}
 * @see {@link Action.js}
 */
Accessibility.CLOSE = new Action({
  name: "Закрыть",
  icon: "eye-invisible",
  href: "#close_accessibility_mode",
  onClick: (event) => {
    Accessibility.onClose(event);
  },
});
